/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { ContentEditableEvent, getUrlSync, RichTextComponent, Text } from '@msdyn365-commerce/core';
import { Address, BusinessPartnerProspect } from '@msdyn365-commerce/retail-proxy';
import { createProspectAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import { EmailRegex } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IHsoBusinessSignUpConfig, IHsoBusinessSignUpProps } from './hso-business-sign-up.props.autogenerated';
import { SignUpButton, SignUpError, SignUpInput, SignUpLabel } from './components';
import { IBusinessSignUpRadioButtonOptionProps, BusinessSignUpRadioButtonOption } from './components/sign-up-radio-button';
import { BusinessSignUpDropdown, IDropdownOption } from './components/sign-up-dropdown';
import SignatureCanvasComponent from './components/sign-up-canvas';
import TermsAndConditions from './components/sign-up-termsandconditions';

export interface IBusinessSignUpExtendedProps extends IHsoBusinessSignUpProps<IHsoBusinessSignUpConfig> {
    formItems?: IFormItem[];
}

export interface IBusinessSignUpItem {
    wrapper: INodeProps;
    isAddressInput?: boolean;
    id: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input?: React.ReactNode;
}

export interface IBusinessState {
    isReady: boolean;
    formItems: IFormItem[];
    addressHasErrors: boolean;
    showAddressErrors: boolean;
    hasSubmitErrors: boolean;
    termsChecked: boolean;
}

export interface IBusinessSignUpLocalAccount {
    FormWrapper: INodeProps;
    items: IBusinessSignUpItem[];
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    disclaimer: React.ReactNode;
}

export interface IBusinessSignUpViewProps {
    className: string;
    businessSignUp: IModuleProps;
    ContainerWrapper: INodeProps;
    heading: React.ReactNode;
    businessSignUpForm: IBusinessSignUpLocalAccount;
    formItems: IFormItem[];
}

export interface IFormItem {
    id: string;
    type: InputType;
    label: string;
    value: string;
    maxChars?: string;
    isRequired: boolean;
    pattern?: string;
    address?: Address;
    customClass?: string;
    fieldReference?: React.RefObject<HTMLInputElement>;
    radioButtonOptions?: IBusinessSignUpRadioButtonOptionProps[];
    radioButtonGroupClass?: string;
    dropdownOptions?: IDropdownOption[];
    dropdownGroupClass?: string;
    customComponent?: React.ReactNode;
    hidden: boolean;
    onChange?: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | { id: string; value: string }
    ) => void;
}

export enum InputType {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- Text enum doesn't override the imported Text
    Text = 'text',
    Address = 'address',
    Email = 'email',
    TextArea = 'textarea',
    RadioButton = 'radiobutton',
    Dropdown = 'dropdown',
    Section = 'section',
    Signature = 'signature',
    Checkbox = 'checkbox'
}

export enum InputID {
    FirstName = 'FirstName',
    LastName = 'LastName',
    CompanyName = 'CompanyName',
    Address = 'CompanyAddress',
    JobTitle = 'JobTitle',
    Size = 'CompanySize',
    Comment = 'Comment',
    ProspectType = 'ProspectType',
    BusinessType = 'BusinessType',
    PSRINumber = 'PSRI Number',
    VATNumber = 'VAT Number',
    VAT56BCertNumber = 'VAT 56B Cert Number',
    CompanyRegistrationNumber = 'Company registration number',
    CustomerType = 'Customer Type',
    StateCustomerType = 'State Customer Type',
    NumberOfEmployees = 'Number of employees',
    RequestedCreditLimit = 'Requested Credit Limit',
    RequestedCreditLimitAmount = 'Requested Credit Limit Amount',
    PurchaseOrderRequired = 'Purchase order required',
    PreferredInvoicingEmailAddress = 'Preferred Invoicing Email Address',
    InvoiceFrequency = 'Invoice Frequency',
    BillingAddress1 = 'Billing address 1',
    BillingAddress2 = 'Billing address 2',
    City = 'City',
    County = 'County',
    Eircode = 'Eircode',
    Country = 'Country',
    MobileNumber = 'MobileNumber',
    DirectorFirstName = 'DirectorFirstName',
    DirectorLastName = 'DirectorLastName',
    DirectorStreetAddress1 = 'DirectorStreetAddress1',
    DirectorStreetAddress2 = 'DirectorStreetAddress2',
    DirectorCity = 'DirectorCity',
    DirectorCounty = 'DirectorCounty',
    DirectorEircode = 'DirectorEircode',
    DirectorCountry = 'DirectorCountry',
    AccountsFirstName = 'AccountsFirstName',
    AccountsLastName = 'AccountsLastName',
    AccountsPosition = 'AccountsPosition',
    AccountsPhone = 'AccountsPhone',
    AccountsEmail = 'AccountsEmail',
    BusinessName1 = 'BusinessName1',
    AddressOrACNo1 = 'AddressOrACNo1',
    PhoneNumber1 = 'PhoneNumber1',
    BusinessName2 = 'BusinessName2',
    AddressOrACNo2 = 'AddressOrACNo2',
    PhoneNumber2 = 'PhoneNumber2',
    PositionWithCompany = 'Department',
    SignatureCanvas = 'SignatureCanvas',
    TermsAndConditions = 'TermsAndConditions',
    Email = 'Email'
}

export const renderInputSection = (
    inputs: IFormItem[],
    className: string,
    needToReset: boolean,
    onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void,
    showErrors: boolean,
    addressForm?: React.ReactNode,
    updateAddress?: (errors: boolean, currAddress?: Address) => void,
    termsChecked?: boolean
): IBusinessSignUpItem[] => {
    return inputs
        .filter(element => !element.hidden)
        .map((element, index) => {
            const {
                id,
                type,
                label,
                maxChars,
                isRequired,
                pattern,
                customClass,
                value,
                fieldReference,
                radioButtonOptions,
                radioButtonGroupClass,
                dropdownOptions,
                customComponent
            } = element;

            const baseSignUpItem: IBusinessSignUpItem = {
                wrapper: {
                    className: classnames(className, `${className}-${id}`, customClass)
                },
                id,
                label: <SignUpLabel {...{ id, forId: id, className, text: label }} />,
                errorMessage: <SignUpError {...{ className, type: 'item' }} />
            };

            if (customComponent) {
                baseSignUpItem.input = <div className={customClass}>{customComponent}</div>;
            } else if (type === InputType.Address) {
                if (addressForm) {
                    baseSignUpItem.isAddressInput = true;
                    baseSignUpItem.input = (
                        <>
                            {React.cloneElement(addressForm as React.ReactElement, {
                                updateForm: updateAddress,
                                hasError: showErrors,
                                isRequired: isRequired === undefined ? false : isRequired,
                                resetAddress: needToReset
                            })}
                        </>
                    );
                } else {
                    // If the address slot is not filled, remove this input from the form
                    inputs.splice(index, 1);
                }
            } else if (type === InputType.Dropdown) {
                baseSignUpItem.input = (
                    <BusinessSignUpDropdown
                        id={id}
                        name={id}
                        label={label}
                        options={dropdownOptions || []}
                        className={`${className}-dropdown`}
                        isRequired={isRequired}
                        value={value}
                        onChange={onInputChange}
                    />
                );
            } else if (type === InputType.RadioButton) {
                baseSignUpItem.input = (
                    <div className={radioButtonGroupClass}>
                        {radioButtonOptions &&
                            radioButtonOptions.map((element, index) => {
                                return <BusinessSignUpRadioButtonOption key={index} {...element}></BusinessSignUpRadioButtonOption>;
                            })}
                    </div>
                );
            } else if (type === InputType.Signature) {
                baseSignUpItem.input = (
                    <SignatureCanvasComponent
                        id={id}
                        label={label}
                        value={value}
                        isRequired={isRequired}
                        className={`${className}-signature`}
                        onChange={(id, base64String) =>
                            onInputChange(({ id, value: base64String } as unknown) as React.ChangeEvent<HTMLInputElement>)
                        }
                    />
                );
            } else if (type === InputType.Section) {
                baseSignUpItem.label = <h2 className={className}>{label}</h2>;
                baseSignUpItem.input = undefined;
            } else if (type === InputType.Checkbox) {
                baseSignUpItem.input = (
                    <input
                        type='checkbox'
                        id={id}
                        checked={termsChecked ?? false} // Pass the value explicitly
                        required={isRequired}
                        onChange={onInputChange}
                    />
                );
            } else {
                baseSignUpItem.input = (
                    <SignUpInput
                        {...{
                            id,
                            type,
                            value,
                            maxLength: maxChars,
                            pattern,
                            className,
                            isRequired: isRequired === undefined ? false : isRequired,
                            onChange: onInputChange,
                            fieldReference
                        }}
                    />
                );
            }

            return baseSignUpItem;
        });
};

/**
 *
 * Business Request Form Modules
 * Form that a business can use to submit a request to become a partner with Microsoft.
 * @extends {React.Component<IHsoBusinessSignUpProps<IHsoBusinessSignUpConfig>>}
 */
class BusinessSignUp extends React.Component<IBusinessSignUpExtendedProps, IBusinessState> {
    private readonly moduleClassName: string = 'ms-business-sign-up';
    //private readonly prospectRadioButtonName: string = 'business-sign-up-radio-button';

    // Used as default data
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public BusinessSignUpDefaultInputs: IFormItem[] = [
        {
            id: 'general',
            type: InputType.Section,
            label: 'General Details',
            value: '',
            isRequired: false,
            customClass: 'width-100',
            hidden: false
        },
        /*         {
            id: InputID.ProspectType,
            type: InputType.RadioButton,
            label: this.props.resources.prospectTypeLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100',
            hidden: false,
            radioButtonGroupClass: 'radio-option-container',
            radioButtonOptions: [
                {
                    id: `${InputID.ProspectType}-buyer`,
                    name: InputID.ProspectType,
                    value: 'Buyer', // Buyer
                    label: 'Standard Account',
                    className: `${this.moduleClassName}__form-item-radio-button-option`,
                    onChange: this._onInputChange.bind(this)
                },
                {
                    id: `${InputID.ProspectType}-seller`,
                    name: InputID.ProspectType,
                    value: 'Seller', // Seller
                    className: `${this.moduleClassName}__form-item-radio-button-option`,
                    label: 'Trade Account',
                    onChange: this._onInputChange.bind(this)
                }
            ]
        }, */
        {
            id: InputID.FirstName,
            type: InputType.Text,
            label: this.props.resources.firstNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: InputID.LastName,
            type: InputType.Text,
            label: this.props.resources.lastNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: InputID.CompanyName,
            type: InputType.Text,
            label: this.props.resources.companyLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false
        },
        {
            id: InputID.BusinessType,
            type: InputType.Dropdown,
            label: this.props.resources.businessTypeLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false,
            dropdownGroupClass: 'dropdown-option-container',
            dropdownOptions: [
                { id: 'default', value: 'default', text: 'Select a Business Type' },
                { id: 'soleTrader', value: 'soleTrader', text: 'Sole Trader' },
                { id: 'limitedCompany', value: 'limitedCompany', text: 'Limited Company' },
                { id: 'partnership', value: 'partnership', text: 'Partnership' },
                { id: 'plc', value: 'plc', text: 'PLC' }
            ],
            onChange: this._onInputChange.bind(this)
        },
        {
            id: InputID.CustomerType,
            type: InputType.Dropdown,
            label: this.props.resources.customerTypeLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false,
            dropdownGroupClass: 'dropdown-option-container',
            dropdownOptions: [
                { id: 'default', value: 'default', text: 'Select a Customer Type' },
                { id: 'electrical', value: 'electrical', text: 'Electrical' },
                { id: 'nonElectrical', value: 'nonElectrical', text: 'Non Electrical' }
            ],
            onChange: this._onInputChange.bind(this)
        },
        {
            id: InputID.PSRINumber,
            type: InputType.Text,
            label: this.props.resources.psriNumberLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50',
            hidden: true
        },
        {
            id: InputID.VATNumber,
            type: InputType.Text,
            label: this.props.resources.vatNumberLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50',
            hidden: true
        },
        {
            id: InputID.VAT56BCertNumber,
            type: InputType.Text,
            label: this.props.resources.vat56bCertNumberLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50',
            hidden: true
        },
        {
            id: InputID.CompanyRegistrationNumber,
            type: InputType.Text,
            label: this.props.resources.companyRegistrationNumberLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50',
            hidden: true
        },
        {
            id: InputID.StateCustomerType,
            type: InputType.Text,
            label: this.props.resources.stateCustomerTypeLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100',
            hidden: true
        },
        {
            id: InputID.NumberOfEmployees,
            type: InputType.Dropdown,
            label: this.props.resources.numberOfEmployeesLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false,
            dropdownGroupClass: 'dropdown-option-container',
            dropdownOptions: [
                { id: 'default', value: 'default', text: 'Select Number of Employees' },
                { id: '1to5', value: '1to5', text: '1 - 5' },
                { id: '6to49', value: '6to49', text: '6 - 49' },
                { id: '50plus', value: '50plus', text: '50+' }
            ]
        },
        {
            id: InputID.PurchaseOrderRequired,
            type: InputType.Dropdown,
            label: this.props.resources.purchaseOrderRequiredLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false,
            dropdownGroupClass: 'dropdown-option-container',
            dropdownOptions: [
                { id: 'default', value: 'default', text: 'Purchase Order Required' },
                { id: 'yes', value: 'yes', text: 'Yes' },
                { id: 'no', value: 'no', text: 'No' }
            ]
        },
        {
            id: InputID.RequestedCreditLimit,
            type: InputType.Dropdown,
            label: this.props.resources.requestedCreditLimitYesNoLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100',
            hidden: false,
            dropdownGroupClass: 'dropdown-option-container',
            dropdownOptions: [
                { id: 'default', value: 'default', text: 'Request Credit Limit?' },
                { id: 'yes', value: 'yes', text: 'Yes' },
                { id: 'no', value: 'no', text: 'No' }
            ]
        },
        {
            id: InputID.RequestedCreditLimitAmount,
            type: InputType.Text,
            label: this.props.resources.requestedCreditLimitLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100',
            hidden: true
        },
        {
            id: InputID.PreferredInvoicingEmailAddress,
            type: InputType.Text,
            label: this.props.resources.preferredInvoicingEmailAddressLabel,
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: InputID.InvoiceFrequency,
            type: InputType.Dropdown,
            label: this.props.resources.invoiceFrequencyLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50 inline-fields',
            hidden: false,
            dropdownGroupClass: 'dropdown-option-container',
            dropdownOptions: [
                { id: 'default', value: 'default', text: 'Select Invoice Frequency' },
                { id: 'weekly', value: 'weekly', text: 'Weekly' },
                { id: 'fortnightly', value: 'fortnightly', text: 'Fortnightly' },
                { id: 'monthly', value: 'monthly', text: 'Monthly' }
            ]
        },
        {
            id: InputID.Address,
            type: InputType.Address,
            label: this.props.resources.billingAddress1Label,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false
        },
        {
            id: InputID.Eircode,
            type: InputType.Text,
            label: this.props.resources.eircodeLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: true
        },
        {
            id: InputID.BillingAddress2,
            type: InputType.Text,
            label: this.props.resources.billingAddress2Label,
            value: '',
            isRequired: false,
            customClass: 'width-100',
            hidden: false
        },
        {
            id: InputID.Email,
            type: InputType.Email,
            label: this.props.resources.emailAddressLabel,
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: InputID.Comment,
            type: InputType.TextArea,
            label: this.props.resources.commentLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100',
            hidden: true
        },
        {
            id: InputID.MobileNumber,
            type: InputType.Text,
            label: this.props.resources.mobileNumberLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: 'director',
            type: InputType.Section,
            label: this.props.resources.sectionDirectorContactInformationLabel,
            value: '',
            isRequired: false,
            hidden: false,
            customClass: 'width-100'
        },
        {
            id: InputID.DirectorFirstName,
            type: InputType.Text,
            label: this.props.resources.directorFirstNameLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: InputID.DirectorLastName,
            type: InputType.Text,
            label: this.props.resources.directorLastNameLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: InputID.DirectorStreetAddress1,
            type: InputType.Text,
            label: this.props.resources.directorStreetAddress1Label,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false
        },
        {
            id: InputID.DirectorStreetAddress2,
            type: InputType.Text,
            label: this.props.resources.directorStreetAddress2Label,
            value: '',
            isRequired: false,
            customClass: 'width-100',
            hidden: false
        },
        {
            id: InputID.DirectorCity,
            type: InputType.Text,
            label: this.props.resources.directorCityLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: InputID.DirectorCounty,
            type: InputType.Text,
            label: this.props.resources.directorCountyLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: InputID.DirectorEircode,
            type: InputType.Text,
            label: this.props.resources.directorEircodeLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: InputID.DirectorCountry,
            type: InputType.Text,
            label: this.props.resources.directorCountryLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: 'account',
            type: InputType.Section,
            label: this.props.resources.sectionAccountsContactLabel,
            value: '',
            isRequired: false,
            hidden: false,
            customClass: 'width-100'
        },
        {
            id: 'accountdescription',
            type: InputType.Section,
            label: this.props.resources.sectionAccountsContactDescriptionLabel,
            value: '',
            isRequired: false,
            hidden: false,
            customClass: 'width-100'
        },
        {
            id: InputID.AccountsFirstName,
            type: InputType.Text,
            label: this.props.resources.accountsFirstNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: InputID.AccountsLastName,
            type: InputType.Text,
            label: this.props.resources.accountsLastNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: InputID.AccountsPosition,
            type: InputType.Text,
            label: this.props.resources.accountsPositionLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false
        },
        {
            id: InputID.AccountsPhone,
            type: InputType.Text,
            label: this.props.resources.accountsPhoneLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: InputID.AccountsEmail,
            type: InputType.Text,
            label: this.props.resources.accountsEmailLabel,
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-50 inline-fields',
            hidden: false
        },
        {
            id: 'trade',
            type: InputType.Section,
            label: this.props.resources.tradeReferencesLabel,
            value: '',
            isRequired: false,
            hidden: false,
            customClass: 'width-100'
        },
        {
            id: 'tradedescription',
            type: InputType.Section,
            label: this.props.resources.tradeReferencesDescription,
            value: '',
            isRequired: false,
            hidden: false,
            customClass: 'width-100'
        },
        {
            id: InputID.BusinessName1,
            type: InputType.Text,
            label: this.props.resources.businessName1Label,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false
        },
        {
            id: InputID.AddressOrACNo1,
            type: InputType.Text,
            label: this.props.resources.addressOrACNo1Label,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false
        },
        {
            id: InputID.PhoneNumber1,
            type: InputType.Text,
            label: this.props.resources.phoneNumber1Label,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false
        },
        {
            id: InputID.BusinessName2,
            type: InputType.Text,
            label: this.props.resources.businessName2Label,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false
        },
        {
            id: InputID.AddressOrACNo2,
            type: InputType.Text,
            label: this.props.resources.addressOrACNo2Label,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false
        },
        {
            id: InputID.PhoneNumber2,
            type: InputType.Text,
            label: this.props.resources.phoneNumber2Label,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false
        },
        {
            id: 'confirmation',
            type: InputType.Section,
            label: this.props.resources.confirmationLabel,
            value: '',
            isRequired: false,
            hidden: false,
            customClass: 'width-100'
        },
        {
            id: InputID.PositionWithCompany,
            type: InputType.Text,
            label: this.props.resources.departmentPositionWithCompany,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false
        },
        {
            id: InputID.SignatureCanvas,
            type: InputType.Signature,
            label: this.props.resources.signatureCanvasLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            hidden: false
        },
        {
            id: InputID.TermsAndConditions,
            type: InputType.Checkbox,
            label: '',
            value: '',
            customComponent: (
                <TermsAndConditions
                    id='terms-checkbox'
                    value={this.state?.termsChecked}
                    onChange={(checked: boolean) => {
                        const updatedFormItems = this.state.formItems.map(item =>
                            item.id === InputID.TermsAndConditions ? { ...item, value: checked ? 'yes' : 'no' } : item
                        );

                        this.setState(
                            {
                                termsChecked: checked,
                                formItems: updatedFormItems
                            },
                            () => {
                                this.setState({ isReady: this._isFormReady() });
                            }
                        );
                    }}
                />
            ),
            isRequired: true,
            customClass: 'width-100',
            hidden: false
        }
    ];

    private readonly formChildReference: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private needsToReset: boolean = false;

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IBusinessSignUpExtendedProps) {
        super(props);

        this.state = {
            isReady: false,
            formItems: this.props.formItems || this._addedReference(),
            addressHasErrors: false,
            showAddressErrors: false,
            hasSubmitErrors: false,
            termsChecked: false
        };

        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        if (!this.state.termsChecked) {
            this.setState({ termsChecked: false });
        }
        document.addEventListener('signatureSaved', this._handleSignatureSaved);
        this.formChildReference.current?.focus();
    }

    public shouldComponentUpdate(nextProps: IBusinessSignUpExtendedProps, nextState: IBusinessState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { config } = this.props;
        const { formItems } = this.state;
        const { className, heading } = config;

        const viewProps = {
            ...this.props,
            businessSignUp: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, className)
            },
            ContainerWrapper: {
                className: `${this.moduleClassName}__container`
            },
            heading: (
                <Text
                    className={`${this.moduleClassName}__heading`}
                    {...heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            businessSignUpForm: this._renderForm(),
            formItems
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle the heading change event.
     * @param event - Content Editable Event.
     */
    public handleHeadingChange = (event: ContentEditableEvent): void => {
        this.props.config.heading.text = event.target.value;
    };

    private _renderForm(): IBusinessSignUpLocalAccount {
        const { config, resources, slots } = this.props;
        const { showAddressErrors, hasSubmitErrors, isReady } = this.state;
        const {
            signUpButtonArialabel,
            signUpButtonText,
            cancelButtonArialabel,
            cancelButtonText,
            errorMessage,
            partialAddressErrorMessage
        } = resources;
        const addressForm = (slots && slots.addressSlot && slots.addressSlot.length > 0 && slots.addressSlot[0]) || undefined;

        const form = {
            FormWrapper: {
                className: `${this.moduleClassName}__form `
            },
            items: renderInputSection.call(
                this,
                this.state.formItems,
                `${this.moduleClassName}__form-item`,
                this.needsToReset,
                this._onInputChange.bind(this),
                showAddressErrors,
                addressForm,
                this._onAddressChange,
                this.state.termsChecked || false
            ),
            buttons: [
                <SignUpButton
                    key='continue'
                    id='continue'
                    className={`${this.moduleClassName}__create-button`}
                    ariaLabel={signUpButtonArialabel}
                    text={signUpButtonText}
                    disabled={!isReady}
                    onClick={this._onSubmit}
                    telemetryContent={this.telemetryContent}
                />,
                <SignUpButton
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={cancelButtonArialabel}
                    text={cancelButtonText}
                    onClick={this._onCancel}
                    telemetryContent={this.telemetryContent}
                />
            ],
            errorMessage: [
                hasSubmitErrors && <SignUpError key='error' id='error' className={this.moduleClassName} message={errorMessage} />,
                showAddressErrors && (
                    <SignUpError key='error' id='error' className={this.moduleClassName} message={partialAddressErrorMessage} />
                )
            ],
            disclaimer: config.disclaimer && (
                <RichTextComponent className={`${this.moduleClassName}__disclaimer`} text={config.disclaimer} />
            )
        };

        if (this.needsToReset) {
            this.needsToReset = false;
        }

        return form;
    }

    private _onInputChange(
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | { id: string; value: string }
    ): void {
        const newFormItems = this.state.formItems.map(item => {
            if ('id' in event) {
                if (item.id === event.id) {
                    return { ...item, value: event.value };
                }
            } else if (item.id === event.target.id || (item.type === InputType.RadioButton && item.id === event.target.name)) {
                if (item.type === InputType.RadioButton) {
                    return { ...item, value: event.target.value };
                }
                return { ...item, value: event.target.value };
            }
            return item;
        });

        const businessType = newFormItems.find(item => item.id === InputID.BusinessType)?.value || 'default';
        const requstedCreditLimit = newFormItems.find(item => item.id === InputID.RequestedCreditLimit)?.value || 'default';

        newFormItems.forEach(item => {
            if (businessType === 'soleTrader') {
                if (item.id === InputID.PSRINumber) {
                    item.isRequired = true;
                    item.hidden = false;
                } else if (
                    [InputID.VATNumber, InputID.VAT56BCertNumber, InputID.CompanyRegistrationNumber, 'director'].includes(
                        item.id as InputID | string
                    )
                ) {
                    item.isRequired = false;
                    item.hidden = true;
                }

                if (
                    [
                        InputID.DirectorFirstName,
                        InputID.DirectorLastName,
                        InputID.DirectorStreetAddress1,
                        InputID.DirectorStreetAddress2,
                        InputID.DirectorCity,
                        InputID.DirectorCounty,
                        InputID.DirectorEircode,
                        InputID.DirectorCountry
                    ].includes(item.id as InputID)
                ) {
                    item.isRequired = false;
                    item.hidden = true;
                }
            } else if (['limitedCompany', 'partnership', 'plc'].includes(businessType)) {
                if (item.id === InputID.VATNumber) {
                    item.isRequired = true;
                    item.hidden = false;
                } else if (item.id === InputID.PSRINumber) {
                    item.isRequired = false;
                    item.hidden = true;
                } else if ([InputID.VAT56BCertNumber, InputID.CompanyRegistrationNumber].includes(item.id as InputID)) {
                    item.hidden = false;
                } else if (item.id === 'director') {
                    item.isRequired = false;
                    item.hidden = false;
                }

                if (
                    [
                        InputID.DirectorStreetAddress1,
                        InputID.DirectorCity,
                        InputID.DirectorCounty,
                        InputID.DirectorEircode,
                        InputID.DirectorCountry
                    ].includes(item.id as InputID)
                ) {
                    item.isRequired = true;
                    item.hidden = false;
                }

                if ([InputID.DirectorFirstName, InputID.DirectorLastName, InputID.DirectorStreetAddress2].includes(item.id as InputID)) {
                    item.isRequired = false;
                    item.hidden = false;
                }
            }
        });

        newFormItems.forEach(item => {
            if (item.id === 'director') {
                item.hidden = !['limitedCompany', 'partnership', 'plc'].includes(businessType);
            }
        });

        const updatedFormItems = newFormItems.map(item => {
            if (item.id === InputID.RequestedCreditLimitAmount) {
                return {
                    ...item,
                    hidden: requstedCreditLimit !== 'yes',
                    value: requstedCreditLimit === 'no' ? '' : item.value
                };
            }
            return item;
        });

        this.setState({
            formItems: [...updatedFormItems],
            isReady: this._isFormReady(),
            hasSubmitErrors: false
        });
    }

    private _addedReference(): IFormItem[] {
        const newFormFields = this.BusinessSignUpDefaultInputs;
        newFormFields[0].fieldReference = this.formChildReference;
        return newFormFields;
    }

    private readonly _onAddressChange = (errors: boolean, currentAddress?: Address): void => {
        const newForm = this.state.formItems;
        const addressItem = newForm.find(item => {
            return item.type === InputType.Address;
        });

        if (addressItem) {
            addressItem.address = currentAddress;

            this.setState(
                {
                    formItems: newForm,
                    addressHasErrors: errors,
                    showAddressErrors: false
                },
                () => {
                    this.setState({ isReady: this._isFormReady() });
                }
            );
        }
    };

    private _isFormReady = (): boolean => {
        const { formItems, addressHasErrors, termsChecked } = this.state;
        const EMPTY_SIGNATURE_BASE64 =
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAAtJREFUGFdjYAACAAAFAAGq1chRAAAAAElFTkSuQmCC';

        for (const currentInput of formItems) {
            if (!currentInput.hidden && currentInput.isRequired) {
                if (currentInput.type === InputType.Address && addressHasErrors) {
                    return false;
                }
                if (currentInput.type !== InputType.Address && (currentInput.value === undefined || currentInput.value === '')) {
                    return false;
                }
                if (currentInput.id === InputID.SignatureCanvas && !currentInput.value) {
                    return false;
                }
                if (currentInput.id === InputID.SignatureCanvas && (!currentInput.value || currentInput.value === EMPTY_SIGNATURE_BASE64)) {
                    return false;
                }
            }

            if (currentInput.pattern !== undefined && currentInput.value.match(currentInput.pattern) === null) {
                return false;
            }
        }

        if (!termsChecked) {
            return false;
        }

        return true;
    };

    private _handleSignatureSaved = () => {
        this.setState({ isReady: this._isFormReady() });
    };

    private readonly _onSubmit = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (this.state.addressHasErrors) {
            this.setState({
                showAddressErrors: true
            });

            event.preventDefault();
            return;
        }

        const { config, context } = this.props;

        // Check for matching id and if not empty
        const firstName = this._getItem(InputID.FirstName);
        const lastName = this._getItem(InputID.LastName);
        const companyName = this._getItem(InputID.CompanyName);
        const companyAddress = this._getItem(InputID.Address, true);
        const jobTitle = this._getItem(InputID.JobTitle);
        const companySize = this._getItem(InputID.Size);
        const comments = this._getItem(InputID.Comment);
        const prospectType = this._getItem(InputID.ProspectType);
        const businessType = this._getItem(InputID.BusinessType);
        const psriNumber = this._getItem(InputID.PSRINumber);
        const vatNumber = this._getItem(InputID.VATNumber);
        const vat56bCertNumber = this._getItem(InputID.VAT56BCertNumber);
        const companyRegistrationNumber = this._getItem(InputID.CompanyRegistrationNumber);
        const customerType = this._getItem(InputID.CustomerType);
        const stateCustomerType = this._getItem(InputID.StateCustomerType);
        const numberOfEmployees = this._getItem(InputID.NumberOfEmployees);
        const requestedCreditLimitYesNo = this._getItem(InputID.RequestedCreditLimit);
        const requestedCreditLimit = this._getItem(InputID.RequestedCreditLimitAmount);
        const purchaseOrderRequired = this._getItem(InputID.PurchaseOrderRequired);
        const preferredInvoicingEmailAddress = this._getItem(InputID.PreferredInvoicingEmailAddress);
        const invoiceFrequency = this._getItem(InputID.InvoiceFrequency);
        const billingAddress1 = this._getItem(InputID.BillingAddress1);
        const billingAddress2 = this._getItem(InputID.BillingAddress2);
        const eircode = this._getItem(InputID.Eircode);
        const mobileNumber = this._getItem(InputID.MobileNumber);
        const directorFirstName = this._getItem(InputID.DirectorFirstName);
        const directorLastName = this._getItem(InputID.DirectorLastName);
        const directorStreetAddress1 = this._getItem(InputID.DirectorStreetAddress1);
        const directorStreetAddress2 = this._getItem(InputID.DirectorStreetAddress2);
        const directorCity = this._getItem(InputID.DirectorCity);
        const directorCounty = this._getItem(InputID.DirectorCounty);
        const directorEircode = this._getItem(InputID.DirectorEircode);
        const directorCountry = this._getItem(InputID.DirectorCountry);
        const accountsFirstName = this._getItem(InputID.AccountsFirstName);
        const accountsLastName = this._getItem(InputID.AccountsLastName);
        const accountsPosition = this._getItem(InputID.AccountsPosition);
        const accountsPhone = this._getItem(InputID.AccountsPhone);
        const accountsEmail = this._getItem(InputID.AccountsEmail);
        const businessName1 = this._getItem(InputID.BusinessName1);
        const addressOrACNo1 = this._getItem(InputID.AddressOrACNo1);
        const phoneNumber1 = this._getItem(InputID.PhoneNumber1);
        const businessName2 = this._getItem(InputID.BusinessName2);
        const addressOrACNo2 = this._getItem(InputID.AddressOrACNo2);
        const phoneNumber2 = this._getItem(InputID.PhoneNumber2);
        const posotionWithCompany = this._getItem(InputID.PositionWithCompany);
        const signatureCanvas = this._getItem(InputID.SignatureCanvas);
        const email = this._getItem(InputID.Email);
        const termsAndConditions = this._getItem(InputID.TermsAndConditions);

        // API gets email as part of the address object, but I do not use the email input from the address component, thus we need to copy it over
        if (email && email.value && companyAddress && companyAddress.address) {
            companyAddress.address.Email = email.value;
        }

        const newPartner: BusinessPartnerProspect = {
            FirstName: firstName && firstName.value,
            LastName: lastName && lastName.value,
            CompanyName: companyName && companyName.value,
            Address: companyAddress && companyAddress.address && companyAddress.address,
            JobTitle: jobTitle && jobTitle.value,
            CompanySize: (companySize && Number.parseInt(companySize.value, 10)) || 0,
            Comments: comments && comments.value,
            ProspectTypeValue: (prospectType && Number.parseInt(prospectType.value, 10)) || 0,
            ExtensionProperties: []
        };

        // Ensure ExtensionProperties is initialized
        newPartner.ExtensionProperties = newPartner.ExtensionProperties || [];

        // if (prospectType && prospectType.value) {
        //     newPartner.ExtensionProperties.push({
        //         Key: 'ProspectType',
        //         Value: { StringValue: prospectType.value.toString() }
        //     });
        // }

        if (businessType && businessType.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOBusinessType',
                Value: { StringValue: businessType.value.toString() }
            });
        }

        if (psriNumber && psriNumber.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOPSRINumber',
                Value: { StringValue: psriNumber.value }
            });
        }

        if (vatNumber && vatNumber.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOVATNumber',
                Value: { StringValue: vatNumber.value }
            });
        }

        if (vat56bCertNumber && vat56bCertNumber.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOVAT56BCertNumber',
                Value: { StringValue: vat56bCertNumber.value }
            });
        }

        if (companyRegistrationNumber && companyRegistrationNumber.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOCompanyRegistrationNumber',
                Value: { StringValue: companyRegistrationNumber.value }
            });
        }
        if (customerType && customerType.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOCustomerType',
                Value: { StringValue: customerType.value.toString() }
            });
        }

        if (stateCustomerType && stateCustomerType.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOCustomerTypeDescription',
                Value: { StringValue: stateCustomerType.value }
            });
        }

        if (numberOfEmployees && numberOfEmployees.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSONumberOfEmployees',
                Value: { StringValue: numberOfEmployees.value.toString() }
            });
        }

        if (requestedCreditLimitYesNo && requestedCreditLimitYesNo.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSORequestedCreditLimit',
                Value: { StringValue: requestedCreditLimitYesNo.value.toString() }
            });
        }

        if (requestedCreditLimit && requestedCreditLimit.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSORequestedCreditLimitAmount',
                Value: { StringValue: requestedCreditLimit.value }
            });
        }

        if (purchaseOrderRequired && purchaseOrderRequired.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOPurchaseOrderRequired',
                Value: { StringValue: purchaseOrderRequired.value }
            });
        }

        if (preferredInvoicingEmailAddress && preferredInvoicingEmailAddress.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOPreferredInvoiceEmailAddress',
                Value: { StringValue: preferredInvoicingEmailAddress.value }
            });
        }

        if (invoiceFrequency && invoiceFrequency.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOInvoiceFrequency',
                Value: { StringValue: invoiceFrequency.value }
            });
        }

        if (billingAddress1 && billingAddress1.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOStreet1',
                Value: { StringValue: billingAddress1.value }
            });
        }

        if (billingAddress2 && billingAddress2.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOStreet2',
                Value: { StringValue: billingAddress2.value }
            });
        }

        if (eircode && eircode.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOEirCode',
                Value: { StringValue: eircode.value }
            });
        }

        if (mobileNumber && mobileNumber.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOPhoneNumber',
                Value: { StringValue: mobileNumber.value }
            });
        }

        if (directorFirstName && directorFirstName.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSODirectorFirstName',
                Value: { StringValue: directorFirstName.value }
            });
        }

        if (directorLastName && directorLastName.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSODirectorLastName',
                Value: { StringValue: directorLastName.value }
            });
        }

        if (directorStreetAddress1 && directorStreetAddress1.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSODirectorStreet1',
                Value: { StringValue: directorStreetAddress1.value }
            });
        }

        if (directorStreetAddress2 && directorStreetAddress2.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSODirectorStreet2',
                Value: { StringValue: directorStreetAddress2.value }
            });
        }

        if (directorCity && directorCity.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSODirectorCity',
                Value: { StringValue: directorCity.value }
            });
        }

        if (directorCounty && directorCounty.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSODirectorCounty',
                Value: { StringValue: directorCounty.value }
            });
        }

        if (directorEircode && directorEircode.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSODirectorEirCode',
                Value: { StringValue: directorEircode.value }
            });
        }

        if (directorCountry && directorCountry.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSODirectorCountry',
                Value: { StringValue: directorCountry.value }
            });
        }

        if (accountsFirstName && accountsFirstName.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOAccountsFirstName',
                Value: { StringValue: accountsFirstName.value }
            });
        }

        if (accountsLastName && accountsLastName.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOAccountsLastName',
                Value: { StringValue: accountsLastName.value }
            });
        }

        if (accountsPosition && accountsPosition.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOAccountsPosition',
                Value: { StringValue: accountsPosition.value }
            });
        }

        if (accountsPhone && accountsPhone.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOAccountsPhone',
                Value: { StringValue: accountsPhone.value }
            });
        }

        if (accountsEmail && accountsEmail.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOAccountsEmail',
                Value: { StringValue: accountsEmail.value }
            });
        }

        if (businessName1 && businessName1.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOTradeReferenceBusinessName',
                Value: { StringValue: businessName1.value }
            });
        }

        if (addressOrACNo1 && addressOrACNo1.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOTradeReferenceAddress',
                Value: { StringValue: addressOrACNo1.value }
            });
        }

        if (phoneNumber1 && phoneNumber1.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOTradeReferencePhoneNumber',
                Value: { StringValue: phoneNumber1.value }
            });
        }

        if (businessName2 && businessName2.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOTradeReferenceBusinessName2',
                Value: { StringValue: businessName2.value }
            });
        }

        if (addressOrACNo2 && addressOrACNo2.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOTradeReferenceAddress2',
                Value: { StringValue: addressOrACNo2.value }
            });
        }

        if (phoneNumber2 && phoneNumber2.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOTradeReferencePhoneNumber2',
                Value: { StringValue: phoneNumber2.value }
            });
        }

        if (posotionWithCompany && posotionWithCompany.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOPositionWithCompany',
                Value: { StringValue: posotionWithCompany.value }
            });
        }

        if (signatureCanvas && signatureCanvas.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOSignature',
                Value: { StringValue: signatureCanvas.value }
            });
        }

        if (termsAndConditions && termsAndConditions.value) {
            newPartner.ExtensionProperties.push({
                Key: 'HSOTermsAndConditions',
                Value: { StringValue: this.state.termsChecked.toString() }
            });
        }

        this.setState({
            isReady: false
        });

        // Create business partner request
        createProspectAsync({ callerContext: context.actionContext }, newPartner)
            .then(() => {
                const successPageURL =
                    (config.confirmationLink && config.confirmationLink.linkUrl && config.confirmationLink.linkUrl.destinationUrl) ||
                    getUrlSync('home', this.props.context.actionContext) ||
                    '';
                window.location.href = successPageURL;
            })
            .catch(() => {
                this.setState({
                    hasSubmitErrors: true
                });
            });
    };

    private readonly _onCancel = () => {
        // Reset values of the form
        const cleanForm = this.state.formItems;
        cleanForm.forEach(input => {
            input.value = '';
        });

        // Set reset flag so address input will get reset on next renderInputSection() call
        this.needsToReset = true;

        this.setState({
            isReady: false,
            formItems: cleanForm,
            addressHasErrors: true,
            showAddressErrors: false,
            hasSubmitErrors: false
        });
    };

    private readonly _getItem = (id: string, isAddress: boolean = false) => {
        if (isAddress) {
            return this.state.formItems.find(item => {
                return item.id === id && item.address;
            });
        }
        return this.state.formItems.find(item => {
            return item.id === id && item.value !== '';
        });
    };
}

export default BusinessSignUp;
